import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileStack } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

export function AssembleClaimPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('claims'));
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branches = user?.branches ?? [];
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [payerId, setPayerId] = useState(params.get('payerId') ?? '');
  const lock = useBranchLock();
  const [branchId, setBranchId] = useState(lock.branchId);
  const [error, setError] = useState<string | null>(null);

  const payers = useQuery({
    queryKey: ['payers'],
    queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers'),
  });

  const coverage = useQuery({
    queryKey: ['coverage-check', patientId, payerId],
    queryFn: () =>
      get<{ covered: boolean; coPayPct: number; reason: string | null; payerName: string | null }>(
        `/insurance/patients/${patientId}/coverage-check${qs({ payerId: payerId || undefined })}`,
      ),
    enabled: Boolean(patientId),
  });

  const create = useMutation({
    mutationFn: () => post('/insurance/claims', { patientId, payerId, ...(branchId ? { branchId } : {}) }),
    onSuccess: () => {
      toast.success('Claim assembled', branchId ? 'Every unclaimed panel invoice of this branch for this patient is on it' : 'Every unclaimed panel invoice for this patient is on it');
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(patientId && payerId);
  const dirty = !create.isSuccess && (patientId !== (params.get('patientId') ?? '') || payerId !== (params.get('payerId') ?? '') || Boolean(branchId));

  return (
    <FormScreen
      title="Assemble a claim"
      description="Pulls every issued invoice with an unclaimed panel share"
      icon={FileStack}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('billing', 'claim')} icon={FileStack}>
        <Field label="Patient" required>
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label="Payer" required>
          <Select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">Select…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
        {branches.length > 1 && (
          <Field label="Branch" hint="A claim carries the bills of one branch. Leave it empty when all of the patient's unclaimed bills are from one branch">
            <Select value={branchId} readOnly={lock.locked} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Choose when needed…</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {coverage.data && (
          <Alert variant={coverage.data.covered ? 'success' : 'warning'} className="sm:col-span-2">
            {coverage.data.covered ? `${coverage.data.payerName} covers this patient at a ${coverage.data.coPayPct}% co-pay.` : coverage.data.reason}
          </Alert>
        )}
      </FormSection>
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormActions submitLabel="Assemble" loading={create.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
